import React, { useContext, useEffect, useState } from "react";
import api from "../../api";
import AuthContext from "../../context/AuthContext";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap/dist/js/bootstrap.bundle.min.js";
import "../../css/quill-fonts.css";

/* ===================== TipTap ===================== */
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import {TextStyle} from "@tiptap/extension-text-style";
import Color from "@tiptap/extension-color";
import TextAlign from "@tiptap/extension-text-align";
import Underline from "@tiptap/extension-underline";
import Highlight from "@tiptap/extension-highlight";
import Subscript from "@tiptap/extension-subscript";
import Superscript from "@tiptap/extension-superscript";
import Link from "@tiptap/extension-link";
import Placeholder from "@tiptap/extension-placeholder";
import FontFamily from "@tiptap/extension-font-family";

import FontSize from "./FontSizeExtension";
import MathExtension from "./MathExtension";
import CustomImageExtension from "./CustomImageExtension";
import MenuBar from "./MenuBar";
/* ================================================= */

export default function CustomerRoleBlogs() {
  const { user } = useContext(AuthContext);

  const [blogs, setBlogs] = useState([]);
  const [selectedBlog, setSelectedBlog] = useState(null);
  const [editingBlog, setEditingBlog] = useState(null);
  const [editTitle, setEditTitle] = useState("");
  const [loading, setLoading] = useState(true);
  const BASE_URL = process.env.REACT_APP_BASE_URL;
const viewEditor = useEditor({
  extensions: [
    StarterKit,
    TextStyle,
    FontFamily,
    FontSize,
    Color,
    Underline,
    Highlight.configure({ multicolor: true }),
    Subscript,
    Superscript,
    Link,
    TextAlign.configure({ types: ["heading", "paragraph"] }),
    MathExtension,
    CustomImageExtension,
  ],
  editable: false, // 🔒 READ ONLY
  content: "",
});
const isTipTapJson = (content) => {
  try {
    const parsed = JSON.parse(content);
    return parsed?.type === "doc";
  } catch {
    return false;
  }
};
  /* ===================== TipTap Editor ===================== */
  const editor = useEditor({
    extensions: [
      StarterKit,
      TextStyle,
      FontFamily,
      FontSize,
      Color,
      Underline,
      Highlight.configure({ multicolor: true }),
      Subscript,
      Superscript,
      Link.configure({ openOnClick: false }),
      TextAlign.configure({ types: ["heading", "paragraph"] }),
      Placeholder.configure({ placeholder: "Edit blog content..." }),
      MathExtension,
      CustomImageExtension,
    ],
    content: "",
  });

  /* ===================== Utils ===================== */
  const stripHtml = (html) => {
    const doc = new DOMParser().parseFromString(html, "text/html");
    return doc.body.textContent || "";
  };
const extractTextFromTipTapJSON = (jsonString) => {
  try {
    const json =
      typeof jsonString === "string"
        ? JSON.parse(jsonString)
        : jsonString;

    let text = "";

    const traverse = (node) => {
      if (!node) return;

      if (node.type === "text" && node.text) {
        text += node.text + " ";
      }

      if (node.content) {
        node.content.forEach(traverse);
      }
    };

    traverse(json);
    return text.trim();
  } catch (e) {
    return "";
  }
};

  /* ===================== Fetch Blogs ===================== */
  useEffect(() => {
    if (!user) return;

    api
      .get("/api/blog/by-customer", {
        params: {
          email: user.username,
          userType: user.customerType,
        },
      })
      .then((res) => setBlogs(res.data || []))
      .finally(() => setLoading(false));
  }, [user]);

  /* ===================== Edit ===================== */
  const handleEdit = (blog) => {
    setEditingBlog(blog);
    setEditTitle(blog.title);

  if (isTipTapJson(blog.textContent)) {
  editor.commands.setContent(JSON.parse(blog.textContent));
} else {
  editor.commands.setContent({
    type: "doc",
    content: [
      {
        type: "paragraph",
        content: [
          {
            type: "text",
            text: blog.textContent,
          },
        ],
      },
    ],
  });
}
  };

  /* ===================== Image Upload (INLINE) ===================== */
  const handleImageUpload = async () => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/*";
    input.click();

    input.onchange = async () => {
      const file = input.files[0];
      if (!file) return;

      const formData = new FormData();
      formData.append("file", file);

      const res = await api.post("/api/blog/upload-image", formData);
      editor.chain().focus().setImage({ src: res.data.url }).run();
    };
  };

  /* ===================== Update ===================== */
  const handleUpdate = async () => {
    if (!editingBlog || !editor) return;

    await api.put(
      `/api/blog/update-my-blog/${editingBlog.id}`,
      {
        title: editTitle,
        textContent: JSON.stringify(editor.getJSON()),
      },
      {
        params: {
          email: user.username,
          userType: user.customerType,
        },
      }
    );

    alert("✅ Blog updated");

    const res = await api.get("/api/blog/by-customer", {
      params: {
        email: user.username,
        userType: user.customerType,
      },
    });

    setBlogs(res.data || []);
    setEditingBlog(null);
    editor.commands.clearContent();
  };

  /* ===================== Delete ===================== */
  const handleDelete = async (id) => {
    if (!window.confirm("Delete this blog?")) return;

    await api.delete(`/api/blog/delete-my-blog/${id}`, {
      params: {
        email: user.username,
        userType: user.customerType,
      },
    });

    const res = await api.get("/api/blog/by-customer", {
      params: {
        email: user.username,
        userType: user.customerType,
      },
    });

    setBlogs(res.data || []);
  };

  if (loading) {
    return <h3 className="text-center mt-5">Loading your blogs...</h3>;
  }

  return (
    <div className="container my-4">
      <h3 className="fw-bold text-primary mb-4">
        📌 Your Blogs ({user.customerType})
      </h3>

      <div className="row g-4">
        {blogs.map((blog) => (
          <div key={blog.id} className="col-md-6 col-lg-4">
            <div className="card h-100 shadow-sm">
              {blog.imageFileName && (
                <img
                  src={`${BASE_URL}/${blog.imageFileName}`}
                  className="card-img-top"
                  style={{ height: 200, objectFit: "cover" }}
                />
              )}

              <div className="card-body">
                <h5>{blog.title}</h5>
              <p className="text-muted small">
  {isTipTapJson(blog.textContent)
    ? extractTextFromTipTapJSON(blog.textContent).slice(0, 120)
    : blog.textContent.replace(/<[^>]*>/g, "").slice(0, 120)}
  ...
</p>
                <div className="d-flex justify-content-between">
                <button
  className="btn btn-primary btn-sm"
  data-bs-toggle="modal"
  data-bs-target="#viewModal"
  onClick={() => {
    setSelectedBlog(blog);

if (viewEditor && blog.textContent) {

  if (isTipTapJson(blog.textContent)) {

    viewEditor.commands.setContent(
      JSON.parse(blog.textContent)
    );

  } else {

    viewEditor.commands.setContent({
      type: "doc",
      content: [
        {
          type: "paragraph",
          content: [
            {
              type: "text",
              text: blog.textContent.replace(/<[^>]*>/g, ""),
            },
          ],
        },
      ],
    });

  }
}  }}
>
  View
</button>


                  <button
                    className="btn btn-warning btn-sm"
                    onClick={() => handleEdit(blog)}
                  >
                    Edit
                  </button>

                  <button
                    className="btn btn-danger btn-sm"
                    onClick={() => handleDelete(blog.id)}
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* ===================== EDIT MODAL ===================== */}
      {editingBlog && (
        <div className="modal fade show d-block" style={{ background: "#0008" }}>
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header bg-warning">
                <h5>Edit Blog</h5>
                <button
                  className="btn-close"
                  onClick={() => setEditingBlog(null)}
                />
              </div>

              <div className="modal-body">
                <input
                  className="form-control mb-3"
                  value={editTitle}
                  onChange={(e) => setEditTitle(e.target.value)}
                />

                <MenuBar editor={editor} onImageUpload={handleImageUpload} />

                <EditorContent
                  editor={editor}
                  className="border rounded p-3"
                />
              </div>

              <div className="modal-footer">
                <button className="btn btn-success" onClick={handleUpdate}>
                  Save
                </button>
                <button
                  className="btn btn-secondary"
                  onClick={() => setEditingBlog(null)}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ===================== VIEW MODAL ===================== */}
     
{/* ===================== VIEW MODAL ===================== */}
<div
  className="modal fade"
  id="viewModal"
  tabIndex="-1"
  aria-hidden="true"
>
  <div className="modal-dialog modal-xl modal-dialog-centered">
    <div className="modal-content rounded-4 shadow-lg">
      {selectedBlog && (
        <>
          {/* HEADER */}
          <div className="modal-header bg-primary text-white">
            <h5 className="modal-title fw-bold">
              {selectedBlog.title}
            </h5>

            <button
              type="button"
              className="btn-close btn-close-white"
              data-bs-dismiss="modal"
              aria-label="Close"
            />
          </div>

          {/* BODY */}
          <div
            className="modal-body"
            style={{ maxHeight: "70vh", overflowY: "auto" }}
          >
            <EditorContent
              editor={viewEditor}
              className="border rounded p-3"
            />
          </div>

          {/* FOOTER */}
          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-secondary rounded-pill px-4"
              data-bs-dismiss="modal"
            >
              Close
            </button>
          </div>
        </>
      )}
    </div>
  </div>
</div>

    </div>
  );
}
